// Matrice de périmètre : exigences × 7 actions (fournir, poser, alimenter, raccorder, programmer, tester, réceptionner).
// Tableau sur ordinateur, cartes sur mobile ; enregistrement automatique ; export XLSX.
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Grid3x3 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { Chargement, EtatErreur, EtatVide, IndicateurEnregistrement, Panneau, useNotifier, type EtatEnregistrement } from '../../components/ui.tsx';
import { BadgeStatutExigence } from '../../components/statuts.tsx';
import { DossierCadre } from './DossierCadre.tsx';
import { ACTIONS, BadgeIncertain, PARTIES, options, type Exigence, type Responsabilite } from './commun.tsx';

export function PagePerimetre() {
  const { id = '' } = useParams();
  const { peut } = useSession();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const ecrire = peut('dossier.ecrire');
  const q = useQuery({ queryKey: ['etudes-exigences', id], queryFn: () => api.get<Exigence[]>(`/api/dossiers/${id}/exigences`) });
  const [masquerExclues, setMasquerExclues] = useState(false);
  const [etat, setEtat] = useState<EtatEnregistrement>('enregistre');
  const [local, setLocal] = useState<Record<string, Record<string, Responsabilite>>>({});

  const resp = (e: Exigence) => local[e.id] ?? Object.fromEntries(e.responsabilites.map((r) => [r.action, r]));
  const changer = async (e: Exigence, action: string, party: string) => {
    const suivant = { ...resp(e), [action]: { action, party, party_name: resp(e)[action]?.party_name ?? null } };
    setLocal((l) => ({ ...l, [e.id]: suivant }));
    setEtat('en_cours');
    try {
      await api.put(`/api/exigences/${e.id}/responsabilites`, { responsabilites: Object.values(suivant).map((r) => ({ action: r.action, party: r.party, partyName: r.party_name || null })) });
      setEtat('enregistre');
      qc.invalidateQueries({ queryKey: ['etudes-exigence', e.id] });
    } catch (err) { setEtat('erreur'); notifier(messageErreur(err), true); }
  };

  const liste = (q.data ?? []).filter((e) => !masquerExclues || e.status !== 'exclu');
  const select = (e: Exigence, a: { code: string; libelle: string }, avecLibelle = false) => {
    const r = resp(e)[a.code];
    const sel = (
      <select className={`saisie et-partie et-partie-${r?.party ?? 'inconnu'}`} value={r?.party ?? 'inconnu'} disabled={!ecrire}
        aria-label={`${e.label} : qui doit ${a.libelle.toLowerCase()} ?`} onChange={(ev) => changer(e, a.code, ev.target.value)}>
        {options(PARTIES).map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle}</option>)}
      </select>
    );
    return avecLibelle ? <label key={a.code} className="et-carte-resp"><span>{a.libelle}</span>{sel}</label> : sel;
  };

  return (
    <DossierCadre dossierId={id} actif="perimetre" large actions={
      <a className="btn" href={`/api/dossiers/${id}/export/perimetre.xlsx`} download><Download aria-hidden />Exporter la matrice (XLSX)</a>}>
      {q.isLoading && <Chargement lignes={6} />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {q.data && q.data.length === 0 && <Panneau><EtatVide icone={<Grid3x3 />} titre="Matrice vide" texte="La matrice se remplit avec les exigences du dossier, après analyse des pièces." /></Panneau>}
      {q.data && q.data.length > 0 && (
        <Panneau serre titre="Qui fournit, pose, alimente, raccorde, programme, teste et réceptionne ?" actions={
          <div className="rangee" style={{ gap: 12, flexWrap: 'wrap' }}>
            <label className="case"><input type="checkbox" checked={masquerExclues} onChange={(e) => setMasquerExclues(e.target.checked)} />Masquer les exclues</label>
            {ecrire && <IndicateurEnregistrement etat={etat} />}
          </div>}>
          <p className="petit secondaire et-legende">« Inconnu » tant que personne n’a tranché. Le nom du lot ou du client se précise dans l’écran Exigences.</p>
          <div className="tableau-conteneur et-matrice-tableau">
            <table className="tableau et-matrice">
              <caption className="visuellement-cache">Matrice de périmètre</caption>
              <thead><tr><th scope="col">Exigence</th>{ACTIONS.map((a) => <th key={a.code} scope="col">{a.libelle}</th>)}</tr></thead>
              <tbody>
                {liste.map((e) => (
                  <tr key={e.id}>
                    <th scope="row" className="et-matrice-exigence">
                      <Link to={`/etudes/${id}/exigences?e=${e.id}`}>{e.label}</Link>
                      <span className="rangee" style={{ gap: 6, flexWrap: 'wrap' }}><BadgeStatutExigence statut={e.status} />{e.other_lot && <span className="tres-petit discret">Lot : {e.other_lot}</span>}</span>
                    </th>
                    {ACTIONS.map((a) => <td key={a.code}>{select(e, a)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="et-matrice-cartes">
            {liste.map((e) => (
              <li key={e.id} className="et-carte">
                <Link to={`/etudes/${id}/exigences?e=${e.id}`} className="et-carte-titre">{e.label}</Link>
                <span className="rangee" style={{ gap: 6, flexWrap: 'wrap' }}><BadgeStatutExigence statut={e.status} />{e.uncertain && <BadgeIncertain />}</span>
                <div className="et-carte-grille">{ACTIONS.map((a) => select(e, a, true))}</div>
              </li>
            ))}
          </ul>
        </Panneau>
      )}
    </DossierCadre>
  );
}
